import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from './client';
import { ensureFreshApiToken } from '../auth/msal';
import { engagementRequirementKeys } from './engagement-requirements';
import { LibraryMetadataSchema } from './requirement-library';
import { uploadWithProgress, type UploadProgressHandlers } from './upload';

/**
 * Requirement spreadsheets (T-0153): engagement import (E11-F06), export
 * (E11-F07) and library intake (E11-F03 scenarios 3 to 5).
 *
 * Contract: `docs/requirement-library/technical/api-requirement-spreadsheets.md`
 * (routes in `apps/api/src/routes/requirement-spreadsheets.ts`).
 *
 *   - A file is uploaded as the raw request body (`application/octet-stream`)
 *     with its name percent-encoded in `X-File-Name`. There is no multipart.
 *     The upload reports its progress (`./upload`, T-0156).
 *   - A preview returns an `import_token`. Changing a mapping is another
 *     preview with `{ import_token, overrides }`: the file is not resent,
 *     and `overrides` always carries every override in effect.
 *   - Confirm needs an `Idempotency-Key`; the caller supplies one per
 *     confirm attempt and reuses it on a retry.
 *   - The engagement and the library previews share most of their shape, so
 *     one schema reads both; fields only one of them sends are optional.
 */

/** The server refuses a larger file; checked here first to save the upload. */
export const MAX_IMPORT_FILE_BYTES = 20 * 1024 * 1024;
export const IMPORT_FILE_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.csv'] as const;

export const IMPORT_FIELDS = [
  'client_ref',
  'module',
  'subcategory',
  'text',
  'criticality',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const SheetSchema = z.object({
  name: z.string(),
  index: z.number().int(),
  used: z.boolean(),
  skip_reason: z.string().nullable(),
  reason: z.string().nullable().optional(),
  row_count: z.number().int(),
  data_row_count: z.number().int(),
  header_row: z.number().int().nullable(),
  module_from_sheet_name: z.string().nullable().optional(),
  module_id: z.string().nullable().optional(),
});
export type ImportSheet = z.infer<typeof SheetSchema>;

const MappingSchema = z.object({
  header: z.string(),
  field: z.string().nullable(),
  confidence: z.string().nullable(),
  source: z.string().nullable(),
  sheets: z.array(z.string()),
});
export type ImportColumnMapping = z.infer<typeof MappingSchema>;

const CriticalityMappingSchema = z.object({
  label: z.string(),
  count: z.number().int(),
  value: z.string().nullable(),
  confidence: z.string().nullable(),
  source: z.string().nullable(),
});
export type ImportCriticalityMapping = z.infer<typeof CriticalityMappingSchema>;

const ModuleMappingSchema = z.object({
  name: z.string(),
  count: z.number().int(),
  source: z.string().nullable().optional(),
  module_id: z.string().nullable(),
  module_name: z.string().nullable(),
  resolved_by: z.string().nullable(),
  skipped: z.boolean(),
  suggestion: z.object({ module_id: z.string(), module_name: z.string() }).nullable().optional(),
});
export type ImportModuleMapping = z.infer<typeof ModuleMappingSchema>;

/** What a row resembles: an existing requirement, or an earlier row of the file. */
const DuplicateSchema = z.object({
  kind: z.string(),
  number: z.string().nullable().optional(),
  module_name: z.string().nullable().optional(),
  same_module: z.boolean().optional(),
  ref: z.string().nullable().optional(),
  sheet: z.string().nullable().optional(),
  row: z.number().int().nullable().optional(),
  client_ref: z.string().nullable().optional(),
  text: z.string(),
  score: z.number(),
  exact: z.boolean(),
  /** Library intake only (T-0154): exact | wording | meaning. */
  match_kind: z.string().optional(),
});
export type ImportDuplicate = z.infer<typeof DuplicateSchema>;

const RefusalSchema = z.object({
  reason: z.string(),
  existing: z
    .object({
      item_id: z.string().optional(),
      number: z.string().optional(),
      ref: z.string().optional(),
      sheet: z.string().optional(),
      row: z.number().int().optional(),
    })
    .nullable()
    .optional(),
});

const RowSchema = z.object({
  ref: z.string(),
  sheet: z.string(),
  row: z.number().int(),
  client_ref: z.string().nullable(),
  module_id: z.string().nullable().optional(),
  module_name: z.string().nullable().optional(),
  source_module_name: z.string().nullable().optional(),
  subcategory: z.string(),
  text: z.string(),
  /** Library only: labelled metadata split off the end of the row's text. */
  metadata: LibraryMetadataSchema.nullable().optional(),
  criticality: z.string().nullable().optional(),
  criticality_label: z.string().nullable().optional(),
  /** `new | duplicate | needs_mapping` (engagement) or `new | duplicate | refused` (library). */
  status: z.string(),
  refusal: RefusalSchema.nullable().optional(),
  duplicates: z.array(DuplicateSchema),
});
export type ImportRow = z.infer<typeof RowSchema>;

const ExcludedSchema = z.object({
  ref: z.string(),
  sheet: z.string(),
  row: z.number().int(),
  client_ref: z.string().nullable(),
  reason: z.string(),
  preview: z.string().nullable().optional(),
  duplicate_of: z
    .object({ ref: z.string(), sheet: z.string(), row: z.number().int() })
    .nullable()
    .optional(),
});
export type ImportExcludedRow = z.infer<typeof ExcludedSchema>;

/** The earlier import of the same bytes. Engagement and library differ. */
const RepeatSchema = z.object({
  load_id: z.string().optional(),
  module_id: z.string().optional(),
  module_name: z.string().nullable().optional(),
  file_name: z.string().nullable().optional(),
  imported_at: z.string(),
  imported_by_name: z.string().nullable().optional(),
  added: z.number().int(),
  still_active: z.number().int().optional(),
  first_number: z.string().nullable().optional(),
  last_number: z.string().nullable().optional(),
});
export type ImportRepeat = z.infer<typeof RepeatSchema>;

const RawImportPreviewSchema = z.object({
  import_token: z.string(),
  expires_at: z.string().optional(),
  file: z.object({
    file_name: z.string(),
    byte_size: z.number().int(),
    retained: z.boolean().optional(),
  }),
  /** Library only: the module the rows go into. */
  module: z
    .object({
      id: z.string(),
      name: z.string(),
      prefix: z.string(),
      draft_revision: z.number().int(),
    })
    .optional(),
  repeat_of: RepeatSchema.nullable(),
  sheets: z.array(SheetSchema),
  mapping: z.array(MappingSchema),
  criticality_mapping: z.array(CriticalityMappingSchema).optional(),
  module_mapping: z.array(ModuleMappingSchema).optional(),
  unknown_modules: z.array(z.string()).optional(),
  unknown_criticality_labels: z.array(z.string()).optional(),
  /** Library only: what the file has that the library never reads. */
  ignored: z
    .object({
      criticality: z.object({ labels: z.array(z.string()) }).nullable(),
      module: z.object({ names: z.array(z.string()) }).nullable(),
    })
    .optional(),
  rows: z.array(RowSchema),
  excluded: z.array(ExcludedSchema),
  counts: z.object({
    sheets_total: z.number().int(),
    sheets_used: z.number().int(),
    rows_read: z.number().int(),
    to_import: z.number().int(),
    likely_duplicates: z.number().int(),
    needs_mapping: z.number().int().optional(),
    refused: z.number().int().optional(),
    excluded: z.number().int(),
    /** Library only: rows whose text ended in labelled metadata. */
    with_metadata: z.number().int().optional(),
  }),
  can_confirm: z.boolean(),
  blockers: z.array(z.string()),
});
type RawImportPreview = z.infer<typeof RawImportPreviewSchema>;

/** A preview, with the lists only the engagement sends always present. */
export type ImportPreview = RawImportPreview & {
  criticality_mapping: ImportCriticalityMapping[];
  module_mapping: ImportModuleMapping[];
  unknown_modules: string[];
  unknown_criticality_labels: string[];
};

function normalisePreview(raw: RawImportPreview): ImportPreview {
  return {
    ...raw,
    criticality_mapping: raw.criticality_mapping ?? [],
    module_mapping: raw.module_mapping ?? [],
    unknown_modules: raw.unknown_modules ?? [],
    unknown_criticality_labels: raw.unknown_criticality_labels ?? [],
  };
}

/** Everything the reviewer changed. Sent whole on every re-preview. */
export interface ImportOverrides {
  sheets?: Record<string, { use?: boolean; header_row?: number; module_id?: string }>;
  column_mapping?: Record<string, ImportField | null>;
  criticality_map?: Record<string, string>;
  module_map?: Record<string, string | null>;
}

export const EngagementImportResultSchema = z.object({
  load_id: z.string(),
  document_id: z.string().nullable().optional(),
  file_name: z.string(),
  added: z.number().int(),
  duplicates_kept: z.number().int(),
  duplicates_skipped: z.number().int(),
  excluded: z.number().int(),
  skipped: z.number().int(),
});
export type EngagementImportResult = z.infer<typeof EngagementImportResultSchema>;

const RefusedRowSchema = z.object({
  ref: z.string(),
  sheet: z.string(),
  row: z.number().int(),
  text: z.string(),
  reason: z.string(),
  existing: z.object({ number: z.string().optional() }).nullable().optional(),
});

export const LibraryImportResultSchema = z.object({
  module_id: z.string(),
  module_name: z.string(),
  file_name: z.string(),
  file_retained: z.boolean().optional(),
  added: z.number().int(),
  first_number: z.string().nullable(),
  last_number: z.string().nullable(),
  draft_revision: z.number().int(),
  duplicates_kept: z.number().int(),
  duplicates_skipped: z.number().int(),
  excluded: z.number().int(),
  refused: z.array(RefusedRowSchema),
});
export type LibraryImportResult = z.infer<typeof LibraryImportResultSchema>;

export const ImportHistoryItemSchema = z.object({
  load_id: z.string(),
  /** `spreadsheet`, or `library_match` for an apply of a library match (T-0263). */
  kind: z.string().optional(),
  file_name: z.string(),
  imported_at: z.string(),
  imported_by: z.string(),
  imported_by_name: z.string().nullable(),
  added: z.number().int(),
  skipped: z.number().int(),
  excluded: z.number().int(),
  duplicates_kept: z.number().int(),
  duplicates_skipped: z.number().int(),
  active: z.number().int(),
  removed: z.number().int(),
  edited: z.number().int(),
  undone: z.boolean(),
});
export type ImportHistoryItem = z.infer<typeof ImportHistoryItemSchema>;

export const ImportHistorySchema = z.object({
  items: z.array(ImportHistoryItemSchema),
  total: z.number().int(),
});

export const UndoImportResultSchema = z.object({
  load_id: z.string(),
  removed: z.number().int(),
  removed_ids: z.array(z.string()),
  kept_edited: z.array(z.object({ id: z.string(), number: z.string(), text: z.string() })),
  already_removed: z.number().int(),
});
export type UndoImportResult = z.infer<typeof UndoImportResultSchema>;

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function engagementBase(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/requirements`;
}

function libraryBase(moduleId: string): string {
  return `/api/v1/requirement-library/modules/${encodeURIComponent(moduleId)}`;
}

/**
 * Send the file itself: raw bytes, the name in a header. Sent with
 * `XMLHttpRequest` (T-0156) so `progress` can report the bytes sent and
 * the moment the last one left; aborting `signal` aborts it.
 */
function uploadForPreview(
  path: string,
  file: File,
  signal?: AbortSignal,
  progress?: UploadProgressHandlers,
): Promise<ImportPreview> {
  return uploadWithProgress(path, file, RawImportPreviewSchema, {
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
    },
    signal,
    ...progress,
  }).then(normalisePreview);
}

function previewAgain(
  path: string,
  importToken: string,
  overrides: ImportOverrides,
  signal?: AbortSignal,
): Promise<ImportPreview> {
  return apiClient(path, RawImportPreviewSchema, {
    method: 'POST',
    body: { import_token: importToken, overrides },
    signal,
  }).then(normalisePreview);
}

export function uploadEngagementImport(
  engagementId: string,
  file: File,
  signal?: AbortSignal,
  progress?: UploadProgressHandlers,
) {
  return uploadForPreview(`${engagementBase(engagementId)}/imports`, file, signal, progress);
}

export function previewEngagementImport(
  engagementId: string,
  importToken: string,
  overrides: ImportOverrides,
  signal?: AbortSignal,
) {
  return previewAgain(`${engagementBase(engagementId)}/imports`, importToken, overrides, signal);
}

export interface ConfirmImportInput {
  importToken: string;
  keepDuplicateRefs: string[];
  acknowledgeRepeat: boolean;
  /** One key per confirm attempt; a retry must reuse it. */
  idempotencyKey: string;
}

function confirmBody(input: ConfirmImportInput): Record<string, unknown> {
  return {
    import_token: input.importToken,
    ...(input.keepDuplicateRefs.length > 0 ? { keep_duplicate_refs: input.keepDuplicateRefs } : {}),
    ...(input.acknowledgeRepeat ? { acknowledge_repeat: true } : {}),
  };
}

export function confirmEngagementImport(
  engagementId: string,
  input: ConfirmImportInput,
): Promise<EngagementImportResult> {
  return apiClient(
    `${engagementBase(engagementId)}/imports:confirm`,
    EngagementImportResultSchema,
    {
      method: 'POST',
      body: confirmBody(input),
      idempotencyKey: input.idempotencyKey,
    },
  );
}

export function listEngagementImports(engagementId: string) {
  return apiClient(`${engagementBase(engagementId)}/imports`, ImportHistorySchema);
}

export function undoEngagementImport(
  engagementId: string,
  loadId: string,
): Promise<UndoImportResult> {
  return apiClient(
    `${engagementBase(engagementId)}/imports/${encodeURIComponent(loadId)}:undo`,
    UndoImportResultSchema,
    { method: 'POST' },
  );
}

export function uploadLibraryImport(
  moduleId: string,
  file: File,
  signal?: AbortSignal,
  progress?: UploadProgressHandlers,
) {
  return uploadForPreview(`${libraryBase(moduleId)}/draft:import-preview`, file, signal, progress);
}

export function previewLibraryImport(
  moduleId: string,
  importToken: string,
  overrides: ImportOverrides,
  signal?: AbortSignal,
) {
  return previewAgain(
    `${libraryBase(moduleId)}/draft:import-preview`,
    importToken,
    overrides,
    signal,
  );
}

export function confirmLibraryImport(
  moduleId: string,
  input: ConfirmImportInput & { baseRevision: number },
): Promise<LibraryImportResult> {
  return apiClient(`${libraryBase(moduleId)}/draft:import`, LibraryImportResultSchema, {
    method: 'POST',
    body: { ...confirmBody(input), base_revision: input.baseRevision },
    idempotencyKey: input.idempotencyKey,
  });
}

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

/** The result carried by an `already_confirmed` conflict, or null. */
export function confirmedResultFromError<T>(err: unknown, schema: z.ZodType<T>): T | null {
  if (!(err instanceof ApiError) || err.code !== 'conflict') return null;
  if (err.details?.['reason'] !== 'already_confirmed') return null;
  const parsed = schema.safeParse(err.details['result']);
  return parsed.success ? parsed.data : null;
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export interface ExportRequirementsQuery {
  module_id?: string;
  criticality?: string;
  source_kind?: string;
  edited?: 'true';
  status?: string;
  q?: string;
  sheets?: 'per_module';
  include_removed?: 'true';
  load_id?: string;
}

export interface ExportedFile {
  blob: Blob;
  fileName: string;
  /** Rows in the file, from `X-Row-Count`; null when the header is absent. */
  rowCount: number | null;
}

/** `filename="..."` of a Content-Disposition header, or null. */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // Fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  return plain?.[1]?.trim() || null;
}

/**
 * Fetch a spreadsheet the API serves (the export, a match review list) as a
 * file. A browser navigation never sends the bearer header, so the bytes are
 * fetched with it and handed to the caller, the same way
 * `fetchDocumentContent` downloads a document.
 */
export async function fetchSpreadsheetFile(
  path: string,
  fallbackName: string,
): Promise<ExportedFile> {
  const correlationId =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const headers: Record<string, string> = {
    Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'X-Correlation-Id': correlationId,
  };
  const token = await ensureFreshApiToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(path, { method: 'GET', headers });
  } catch (err) {
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
    try {
      body = (await res.json()) as typeof body;
    } catch {
      body = {};
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? 'internal',
      body.error?.message ?? `The export failed with status ${res.status}.`,
      correlationId,
      body.error?.details,
    );
  }
  const count = Number(res.headers.get('x-row-count'));
  return {
    blob: await res.blob(),
    fileName: fileNameFromDisposition(res.headers.get('content-disposition')) ?? fallbackName,
    rowCount: res.headers.get('x-row-count') != null && Number.isFinite(count) ? count : null,
  };
}

/** The engagement's requirements as .xlsx: all of them, or the query's view. */
export function exportEngagementRequirements(
  engagementId: string,
  query: ExportRequirementsQuery,
): Promise<ExportedFile> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return fetchSpreadsheetFile(
    `${engagementBase(engagementId)}:export${qs ? `?${qs}` : ''}`,
    'requirements.xlsx',
  );
}

// ---------------------------------------------------------------------------
// Query + mutation hooks
// ---------------------------------------------------------------------------

export const requirementImportKeys = {
  history: (engagementId: string) =>
    [...engagementRequirementKeys.all(engagementId), 'imports'] as const,
};

export function useEngagementImportsQuery(engagementId: string, enabled: boolean) {
  return useQuery({
    queryKey: requirementImportKeys.history(engagementId),
    queryFn: () => listEngagementImports(engagementId),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

/** Undo one import, then refetch the table and the history. */
export function useUndoEngagementImportMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (loadId: string) => undoEngagementImport(engagementId, loadId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: engagementRequirementKeys.all(engagementId) }),
  });
}
